import { ApolloClient, ApolloQueryResult } from 'apollo-client';
import { ApolloLink, DocumentNode, FetchResult } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { RetryLink } from 'apollo-link-retry';
import { setContext } from 'apollo-link-context';
import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import Vue from 'vue';

export enum MicroserviceEnum {
  ARCANEUM = 'arcaneum',
  ADMISSIONS = 'admissions',
}

export enum SignUpMethod {
  accounts = 'accounts',
  enrollments = 'enrollments',
}

const URL: Record<MicroserviceEnum, string | undefined> = {
  arcaneum: process.env.VUE_APP_WESTEROS_ARCANEUM_GRAPHQL,
  admissions: process.env.VUE_APP_WESTEROS_ADMISSIONS_GRAPHQL,
};

export const westerosApolloClient = {} as Record<
  MicroserviceEnum,
  ApolloClient<NormalizedCacheObject>
>;

export const authLink = setContext((_, { headers }) => {
  const authToken = Vue.$cookies.get('admissions-auth-token');

  const authorization = authToken ? `Bearer ${authToken.replace(/"/g, '')}` : undefined;

  return { headers: { ...headers, authorization } };
});

export function getWesterosApolloClient(
  microservice: MicroserviceEnum,
): ApolloClient<NormalizedCacheObject> {
  if (!westerosApolloClient[microservice]) {
    const uri = URL[microservice];

    const link = ApolloLink.from([new RetryLink(), authLink, createHttpLink({ uri })]);

    westerosApolloClient[microservice] = new ApolloClient({
      link,
      cache: new InMemoryCache(),
      connectToDevTools: process.env.NODE_ENV === 'development',
      defaultOptions: { query: { fetchPolicy: 'no-cache' } },
    });
  }

  return westerosApolloClient[microservice];
}

export async function requestWithCache<T>(
  fn: Function,
  query: DocumentNode,
  variables?: { [key: string]: any },
  cacheTimeMinutes: number = 3,
): Promise<T> {
  const timestamp = Date.now();
  let queryName;
  let variablesHash;
  let itemKey = '';

  try {
    // @ts-ignore
    queryName = query.definitions[0].name.value;
    variablesHash = atob(JSON.stringify(variables) || '');

    itemKey = `${queryName}${variablesHash}`;

    const storageData = localStorage.getItem(itemKey);

    if (storageData) {
      const storageObject: { timestamp: number } = JSON.parse(storageData);
      const minutesDiff = Math.floor((timestamp - storageObject.timestamp) / 1000 / 60);

      if (minutesDiff < cacheTimeMinutes) {
        const { timestamp, ...data } = storageObject;
        return data as T;
      }
    }
  } catch {
    // In case of any error, we always do the request.
  }

  const response = await fn({ query, variables });

  try {
    if (itemKey && Object.keys(response.data).length) {
      localStorage.setItem(itemKey, JSON.stringify({ timestamp, ...response }));
    }
  } catch {
    // In case of any error, we always return response.
  }

  return response;
}

export async function query<T>(
  microservice: MicroserviceEnum,
  query: DocumentNode,
  variables?: Record<string, any>,
  hasCache?: boolean,
  cacheTimeMinutes?: number,
): Promise<ApolloQueryResult<T>> {
  const apolloClient = getWesterosApolloClient(microservice);

  return hasCache
    ? requestWithCache<ApolloQueryResult<T>>(apolloClient.query, query, variables, cacheTimeMinutes)
    : apolloClient.query<T>({
        query,
        variables,
      });
}

type KeyValue = { [key: string]: any };
export type Mutation<T> = (
  microservice: MicroserviceEnum,
  mutation: DocumentNode,
  variables: KeyValue,
) => Promise<FetchResult<T>>;

export async function mutation<T>(
  microservice: MicroserviceEnum,
  mutation: DocumentNode,
  variables?: { [key: string]: any },
): Promise<FetchResult<T>> {
  const apolloClient = getWesterosApolloClient(microservice);

  return apolloClient.mutate<T>({
    mutation,
    variables,
  });
}
